import React from "react";
import {
  clearAuthCallbackParams,
  getAuthCallbackParams,
  hasAuthCallbackParams,
  resolveAuthSessionFromLocation,
} from "./authCallback";
import { supabase } from "./supabaseClient";

const MIN_PASSWORD_LENGTH = 6;
const SPECIAL_CHARACTER_REGEX = /[^A-Za-z0-9]/;

type PageState =
  | { status: "loading" }
  | { status: "ready" }
  | { status: "submitting" }
  | { status: "success" }
  | { status: "error"; message: string };

function Logo() {
  return (
    <a href="/" className="flex items-center gap-2 no-underline">
      <span className="font-['Figtree',sans-serif] font-black text-[#07001a] text-[20px] tracking-[-0.4px]">
        evo
      </span>
    </a>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-[24px] shadow-[0px_8px_32px_rgba(0,0,0,0.08)] p-8 w-full max-w-[440px] relative">
      <div
        aria-hidden="true"
        className="absolute border border-[rgba(0,6,20,0.05)] border-solid inset-0 pointer-events-none rounded-[24px]"
      />
      {children}
    </div>
  );
}

async function createRecoverySession() {
  if (!supabase) {
    throw new Error("Password reset is not configured.");
  }
  return resolveAuthSessionFromLocation({ allowRecoveryToken: true });
}

function getPasswordError(password: string, confirmPassword: string) {
  if (password.length < MIN_PASSWORD_LENGTH) {
    return `Use at least ${MIN_PASSWORD_LENGTH} characters.`;
  }
  if (!SPECIAL_CHARACTER_REGEX.test(password)) {
    return "Include at least one special character.";
  }
  if (password !== confirmPassword) {
    return "Passwords do not match.";
  }
  return "";
}

export default function ResetPassword() {
  const handledCallback = React.useRef(false);
  const [page, setPage] = React.useState<PageState>({ status: "loading" });
  const [password, setPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [submitted, setSubmitted] = React.useState(false);

  React.useEffect(() => {
    if (handledCallback.current) return;
    handledCallback.current = true;

    const params = getAuthCallbackParams();
    createRecoverySession()
      .then((session) => {
        if (!session) {
          setPage({
            status: "error",
            message: "This reset link is invalid or has expired.",
          });
          return;
        }

        if (hasAuthCallbackParams(params)) clearAuthCallbackParams();
        setPage({ status: "ready" });
      })
      .catch((error) => {
        setPage({
          status: "error",
          message:
            error instanceof Error
              ? error.message
              : "This reset link is invalid or has expired.",
        });
      });
  }, []);

  const validationError = getPasswordError(password, confirmPassword);
  const showValidation = submitted || password.length > 0 || confirmPassword.length > 0;
  const isSubmitting = page.status === "submitting";

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitted(true);

    if (!supabase || validationError || isSubmitting) return;

    try {
      setPage({ status: "submitting" });
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      setPassword("");
      setConfirmPassword("");
      setPage({ status: "success" });
    } catch (error) {
      setPage({
        status: "error",
        message:
          error instanceof Error
            ? error.message
            : "We could not update your password. Please request a new reset link.",
      });
    }
  };

  return (
    <div className="min-h-screen bg-[#faf9fb] flex flex-col">
      <header className="flex items-center justify-between px-6 py-4">
        <Logo />
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-12">
        {page.status === "loading" && (
          <Card>
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-[#07001a] border-t-transparent rounded-full animate-spin" />
            </div>
          </Card>
        )}

        {(page.status === "ready" || page.status === "submitting") && (
          <Card>
            <h1 className="font-['Figtree',sans-serif] font-black text-[#07001a] text-[22px] tracking-[-0.44px] mb-3">
              Set a new password
            </h1>
            <p className="font-['Figtree',sans-serif] text-[#776f7b] text-[15px] leading-relaxed mb-6">
              Enter a new password for your Evo account.
            </p>

            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <label className="flex flex-col gap-2">
                <span className="font-['Figtree',sans-serif] font-semibold text-[#07001a] text-[14px]">
                  New password
                </span>
                <input
                  autoComplete="new-password"
                  className="w-full rounded-[12px] border border-[rgba(7,0,26,0.14)] bg-white px-4 py-3 font-['Figtree',sans-serif] text-[#07001a] text-[15px] outline-none focus:border-[#ff381a]"
                  disabled={isSubmitting}
                  onChange={(event) => setPassword(event.target.value)}
                  type="password"
                  value={password}
                />
              </label>

              <label className="flex flex-col gap-2">
                <span className="font-['Figtree',sans-serif] font-semibold text-[#07001a] text-[14px]">
                  Confirm password
                </span>
                <input
                  autoComplete="new-password"
                  className="w-full rounded-[12px] border border-[rgba(7,0,26,0.14)] bg-white px-4 py-3 font-['Figtree',sans-serif] text-[#07001a] text-[15px] outline-none focus:border-[#ff381a]"
                  disabled={isSubmitting}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  type="password"
                  value={confirmPassword}
                />
              </label>

              {showValidation && validationError && (
                <p className="font-['Figtree',sans-serif] text-[#c62828] text-[13px] leading-relaxed">
                  {validationError}
                </p>
              )}

              <button
                className="w-full bg-[#07001a] text-white font-['Figtree',sans-serif] font-semibold text-[15px] py-3 rounded-[12px] transition-opacity disabled:opacity-50 hover:opacity-90 active:opacity-80"
                disabled={isSubmitting || Boolean(validationError)}
                type="submit"
              >
                {isSubmitting ? "Updating..." : "Update password"}
              </button>
            </form>
          </Card>
        )}

        {page.status === "success" && (
          <Card>
            <h1 className="font-['Figtree',sans-serif] font-black text-[#07001a] text-[22px] tracking-[-0.44px] mb-3">
              Password updated
            </h1>
            <p className="font-['Figtree',sans-serif] text-[#776f7b] text-[15px] leading-relaxed">
              You can now sign in to Evo with your new password.
            </p>
          </Card>
        )}

        {page.status === "error" && (
          <Card>
            <h1 className="font-['Figtree',sans-serif] font-black text-[#07001a] text-[22px] tracking-[-0.44px] mb-3">
              Reset link failed
            </h1>
            <p className="font-['Figtree',sans-serif] text-[#776f7b] text-[15px] leading-relaxed">
              {page.message}
            </p>
          </Card>
        )}
      </main>

      <footer className="text-center pb-8">
        <p className="font-['Figtree',sans-serif] text-[#776f7b] text-[13px]">
          evograding.com &middot;{" "}
          <a
            href="mailto:hello@evograding.com"
            className="underline text-[#776f7b]"
          >
            hello@evograding.com
          </a>
        </p>
      </footer>
    </div>
  );
}
